<script setup lang="ts">
import { FeatureBanner, type Icon } from '@teachino/shared-design-system';
import { $t } from '@teachino/shared-i18n';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { computed } from 'vue';
import type { UpsellFeatureBannerProps } from './upsell-feature-banner';

const DEFAULT_UPSELL_ICON: Icon = 'crown';
const DEFAULT_UPSELL_ACTION_ICON: Icon = 'open_in_new';

const props = withDefaults(defineProps<UpsellFeatureBannerProps>(), {
  showBetaTag: false,
  embedded: false,
});

const di = useGlobalDependencyProvider();
const analytics = di.inject('analytics');
const router = di.inject('router');

const wrapperClass = computed(() =>
  props.embedded ? 'w-full' : 'mx-auto mb-10 w-full max-w-4xl',
);

const bannerTitle = computed(
  () => props.title ?? $t('upgrade.feature_discovered.title'),
);

const bannerDescription = computed(() => {
  const baseDescription =
    props.description ?? $t('upgrade.feature_discovered.description');

  if (props.daysLeft === undefined || !props.softPaywallPeriod) {
    return baseDescription;
  }

  const daysLeftText =
    props.softPaywallPeriod === 'trial'
      ? $t('upgrade.soft_paywall.trial_days_left', {
          days: props.daysLeft.toString(),
        })
      : $t('upgrade.soft_paywall.grace_days_left', {
          days: props.daysLeft.toString(),
        });

  return `${baseDescription} ${daysLeftText}`;
});

const bannerAriaLabel = computed(
  () => props.ariaLabel ?? $t('upgrade.feature_discovered.title'),
);

const bannerIcon = computed(() => props.icon ?? DEFAULT_UPSELL_ICON);

const bannerActionLabel = computed(
  () => props.actionLabel ?? $t('upgrade.feature_discovered.cta'),
);

const bannerActionIcon = computed(
  () => props.actionIcon ?? DEFAULT_UPSELL_ACTION_ICON,
);

const handleUpgrade = (): void => {
  if (!props.upgradeHandler) {
    analytics.track('upgrade-open-dialog', { source: props.source });
  }
  if (props.upgradeHandler) {
    props.upgradeHandler();
    return;
  }
  router.upgrade();
};
</script>

<template>
  <div :class="wrapperClass">
    <FeatureBanner
      :title="bannerTitle"
      :description="bannerDescription"
      :icon="bannerIcon"
      :aria-label="bannerAriaLabel"
      :show-beta-tag="props.showBetaTag"
      :action-label="bannerActionLabel"
      :action-icon="bannerActionIcon"
      :action-layout="props.actionLayout"
      @action="handleUpgrade"
    />
  </div>
</template>
